import { RowSelectionState } from "@tanstack/react-table";
import {
    EditorRowCapabilities,
    GetRowCapabilities,
} from "../editorCapabilities";
import { NestedEditorRow } from "./nestedRowTypes";

export const rowConstituentDraftIds = (row: NestedEditorRow): string[] =>
    row.kind === "group"
        ? row.groups.map((group) => group.id)
        : row.accounts.map((account) => account.id);

export const selectionToDraftIds = (
    rows: NestedEditorRow[],
    rowSelection: RowSelectionState,
): string[] => {
    const draftIds: string[] = [];
    const visit = (row: NestedEditorRow): void => {
        if (rowSelection[row.id]) {
            draftIds.push(...rowConstituentDraftIds(row));
        }
        row.children?.forEach(visit);
    };
    rows.forEach(visit);
    return draftIds;
};

// A gesture on a merged row is only as capable as its most-restricted
// constituent.
export const orRowCapabilities = (
    row: NestedEditorRow,
    getRowCapabilities: GetRowCapabilities,
): EditorRowCapabilities => {
    const constituents = row.accounts.map((account) => ({
        account,
        capabilities: getRowCapabilities(account),
    }));
    const firstReason = (
        pick: (capabilities: EditorRowCapabilities) => string | null,
    ): string | null => {
        for (const { capabilities } of constituents) {
            const reason = pick(capabilities);
            if (reason != null) {
                return reason;
            }
        }
        return null;
    };
    const firstPlaceholder = (): EditorRowCapabilities["placeholder"] => {
        for (const { capabilities } of constituents) {
            if (capabilities.placeholder != null) {
                return capabilities.placeholder;
            }
        }
        return null;
    };
    // OR-ing hasOpeningBalance (not AND) keeps Remove opening balance offered on
    // a mixed row where only some businesses carry a balance.
    const hasOpeningBalance = constituents.some(
        ({ capabilities }) => capabilities.hasOpeningBalance,
    );
    const hasChartSeed = constituents.some(
        ({ capabilities }) => capabilities.hasChartSeed,
    );
    return {
        renameBlockReason: firstReason((c) => c.renameBlockReason),
        codeEditBlockReason: firstReason((c) => c.codeEditBlockReason),
        typeChangeBlockReason: firstReason((c) => c.typeChangeBlockReason),
        archiveBlockReason: firstReason((c) => c.archiveBlockReason),
        deleteBlockReason: firstReason((c) => c.deleteBlockReason),
        // The representative account only (the chart page's `account`), never a
        // later constituent's reason: null when the row has no representative
        // (group / type rows).
        mergeBlockReason: row.account
            ? getRowCapabilities(row.account).mergeBlockReason
            : null,
        hasOpeningBalance,
        hasChartSeed,
        placeholder: firstPlaceholder(),
        // Representative only; Reassign visibility walks constituents instead.
        role: row.account ? getRowCapabilities(row.account).role : null,
    };
};
